import { useEffect, useMemo, useState } from 'react'
import {
  AlertTriangle,
  CalendarClock,
  GitBranch,
  RefreshCw,
  ShieldAlert,
} from 'lucide-react'
import { useOperatorStore } from '../../stores/operatorStore'
import { EvidenceDrawer } from './EvidenceDrawer'
import { GanttHierarchy, buildGanttRows } from './GanttHierarchy'
import { GanttTimeline, visibleTimelineRecords } from './GanttTimeline'
import { OperatorFilters } from './OperatorFilters'

interface CommunicationGanttProps {
  projectId: string
  projectLabel?: string
}

function readable(value: string): string {
  const normalized = value
    .replace(/_/g, ' ')
    .toLowerCase()
  return normalized.charAt(0).toUpperCase() + normalized.slice(1)
}

export function CommunicationGantt({
  projectId,
  projectLabel,
}: CommunicationGanttProps) {
  const timeline = useOperatorStore((state) => state.timeline)
  const filters = useOperatorStore((state) => state.filters)
  const zoom = useOperatorStore((state) => state.zoom)
  const loading = useOperatorStore((state) => state.loading.timeline)
  const error = useOperatorStore((state) => state.errors.timeline)
  const loadTimeline = useOperatorStore((state) => state.loadTimeline)
  const setFilter = useOperatorStore((state) => state.setFilter)
  const setZoom = useOperatorStore((state) => state.setZoom)
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null)
  const [expandedTeamIds, setExpandedTeamIds] = useState<Set<string>>(
    () => new Set(),
  )

  useEffect(() => {
    setExpandedTeamIds(new Set())
    setSelectedEventId(null)
    void loadTimeline(projectId)
  }, [loadTimeline, projectId])

  const activeTimeline = timeline?.projectId === projectId ? timeline : null
  const rows = useMemo(
    () => activeTimeline
      ? buildGanttRows(
          activeTimeline,
          projectLabel ?? `Project · ${activeTimeline.projectId}`,
          filters,
          expandedTeamIds,
        )
      : [],
    [activeTimeline, expandedTeamIds, filters, projectLabel],
  )
  const selectedEvent = activeTimeline?.events.find(
    (event) => event.id === selectedEventId,
  ) ?? null
  const visibleRisks = useMemo(
    () => activeTimeline
      ? visibleTimelineRecords(activeTimeline, rows, filters).risks
      : [],
    [activeTimeline, filters, rows],
  )

  if (!activeTimeline && loading) {
    return (
      <div
        role="status"
        aria-label="Loading Communication Gantt"
        className="grid min-h-72 animate-pulse place-items-center rounded-lg border border-border bg-muted/20 motion-reduce:animate-none"
      >
        <span className="text-xs text-muted-foreground">Loading project evidence ledger…</span>
      </div>
    )
  }

  if (!activeTimeline && error) {
    return (
      <div
        role="alert"
        className="flex min-h-56 flex-col items-center justify-center gap-3 rounded-lg border border-destructive/30 bg-destructive/5 p-6 text-center"
      >
        <ShieldAlert size={22} className="text-destructive" aria-hidden="true" />
        <div>
          <p className="text-sm font-semibold">Communication timeline unavailable</p>
          <p className="mt-1 text-xs text-muted-foreground">{error}</p>
        </div>
        <button
          type="button"
          aria-label="Retry timeline"
          onClick={() => void loadTimeline(projectId)}
          className="inline-flex items-center gap-2 rounded-md bg-primary px-3 py-2 text-xs font-semibold text-primary-foreground outline-none hover:bg-primary/90 focus-visible:ring-2 focus-visible:ring-primary/50"
        >
          <RefreshCw size={12} aria-hidden="true" />
          Retry
        </button>
      </div>
    )
  }

  if (!activeTimeline) return null

  return (
    <section
      aria-label="Live Communication Gantt"
      className="overflow-hidden rounded-lg border border-border bg-card"
    >
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-4 py-3">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-primary">
            Evidence-backed projection
          </p>
          <h3 className="mt-0.5 text-sm font-semibold text-foreground">
            {projectLabel ?? activeTimeline.projectId}
          </h3>
        </div>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[10px] text-muted-foreground">
          <span className="inline-flex items-center gap-1.5">
            <CalendarClock size={11} aria-hidden="true" />
            {activeTimeline.events.length} exact events
          </span>
          <span className="inline-flex items-center gap-1.5">
            <GitBranch size={11} aria-hidden="true" />
            {activeTimeline.dependencies.length} dependencies
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="size-2 rotate-45 border border-muted-foreground" />
            Baseline
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="size-2 rotate-45 bg-primary" />
            Current plan
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="size-2 rounded-full bg-foreground" />
            Fact
          </span>
        </div>
      </div>

      {visibleRisks.length > 0 && (
        <div className="flex gap-2 overflow-x-auto border-b border-border bg-destructive/5 px-3 py-2">
          {visibleRisks.map((risk) => (
            <div
              key={`${risk.code}:${risk.commitmentId}:${risk.dependencyId}`}
              className="flex min-w-60 flex-1 items-start gap-2 border-l-2 border-destructive px-2"
            >
              <AlertTriangle size={12} className="mt-0.5 shrink-0 text-destructive" aria-hidden="true" />
              <div className="min-w-0">
                <p className="flex items-center justify-between gap-2 text-[10px] font-semibold text-destructive">
                  <span>{readable(risk.code)}</span>
                  <span>{Math.round(risk.confidence * 100)}% confidence</span>
                </p>
                <p className="mt-0.5 truncate text-[10px] text-muted-foreground" title={risk.reason}>
                  {risk.reason}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}

      <OperatorFilters
        timeline={activeTimeline}
        filters={filters}
        zoom={zoom}
        onFilterChange={setFilter}
        onZoomChange={setZoom}
      />

      <div
        data-testid="communication-gantt-layout"
        className="grid grid-cols-1 lg:grid-cols-[minmax(220px,288px)_minmax(0,1fr)]"
      >
        <GanttHierarchy
          rows={rows}
          expandedTeamIds={expandedTeamIds}
          onTeamToggle={(teamId) => {
            setExpandedTeamIds((current) => {
              const next = new Set(current)
              if (next.has(teamId)) next.delete(teamId)
              else next.add(teamId)
              return next
            })
          }}
        />
        <div className="min-w-0 overflow-x-auto overscroll-x-contain">
          <GanttTimeline
            timeline={activeTimeline}
            rows={rows}
            filters={filters}
            zoom={zoom}
            onEventSelect={setSelectedEventId}
          />
        </div>
      </div>

      {loading && (
        <div role="status" className="border-t border-border px-4 py-2 text-[10px] text-muted-foreground">
          Refreshing timeline…
        </div>
      )}
      {error && (
        <p role="alert" className="border-t border-border px-4 py-2 text-xs text-destructive">
          {error}
        </p>
      )}

      {selectedEvent && (
        <EvidenceDrawer
          event={selectedEvent}
          timeline={activeTimeline}
          onClose={() => setSelectedEventId(null)}
        />
      )}
    </section>
  )
}
